import AppCommandBar from 'app/AppCommandBar'
import { FlexSpinner } from 'components'
import { ViewAuthorizer } from 'components/AccessDenied'
import { StickyPane } from 'components/StickyPane'
import { FormikActions } from 'formik'
import { ProvingDetailsTimespanForm } from 'forms/ProvingDetailsForm'
import { useCommand, useShowCommand } from 'hooks/useCommand'
import { useFormikPanelState } from 'hooks/useFormikPanelState'
import useSelection from 'hooks/useSelection'
import {
  ResourceBooking,
  ResourceBookingBatchInput,
  ResourceBookingUpdateInput,
  User,
  UserRole,
  Vehicle,
  VehicleBatchInput,
  VehicleUpdateInput,
} from 'model'
import { FormikPanel } from 'panels'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useRouter } from 'react-router5'
import { useUpdateVehiclesMutation } from 'views/ProvingDetailsView/hooks'
import {
  useReadProvingsForResourceBookingOverviewQuery,
  useUpdateResourceBookingMutation,
} from './ResourceBookingsView.generated'
import {
  ResourceOfficeBookingsViewList,
  ResourceVehicleBookingsViewList,
} from './ResourceBookingsViewList'

// export interface ResourceOfficeBookingListItem {
//   item: ResourceBooking | undefined
//   provingTitle: string
//   provingNumbr: string
// }

export type ResourceOfficeBookingListItem = ResourceBooking & {
  provingGround?: string
  proving?: string
  provingId?: any
  provingTitel?: string
  responsible?: User
  provingDateFrom?: Date
  provingDateTo?: Date
}

export type ResourceVehicleBookingListItem = Vehicle & {
  provingGround?: string
  proving?: string
  provingId?: any
  provingTitel?: string
  responsible?: User
  provingDateFrom?: Date
  provingDateTo?: Date
}

export enum GroupType {
  OfficeLocation,
  OfficeDate,
  VehicleModell,
  VehicleNumber,
}

const getResourceBookings = (
  provings: any
): ResourceOfficeBookingListItem[] => {
  const resourceBookings: ResourceOfficeBookingListItem[] = []
  provings.forEach((proving: any) => {
    const currentResourceBookings = proving.resourceBookings
      ? proving.resourceBookings.filter(
          (item: any) => item.resource && item.resource.type === 'workstation'
        )
      : []

    currentResourceBookings.forEach((resource: ResourceBooking) => {
      const item: ResourceOfficeBookingListItem = resource
      item.provingTitel = proving.title
      item.proving = proving.id
      item.provingGround = proving.provingGround.title
      item.responsible = proving.responsible
        ? proving.responsible[0]
        : undefined
      item.provingDateFrom = proving.dateFrom
      item.provingDateTo = proving.dateTo
      item.provingId = proving._id

      resourceBookings.push(item)
    })
  })
  return resourceBookings
}

const ResourceOfficeLocationBookingsListView: React.FunctionComponent = () => {
  const { t } = useTranslation()
  const [updateResourceBooking] = useUpdateResourceBookingMutation()

  const [timespanPanelState, showTimespanPanel, hideTimespanPanel] =
    useFormikPanelState()

  const [selection, selectedItems] =
    useSelection<ResourceOfficeBookingListItem>({
      getKey: (item) => item._id,
    })

  const router = useRouter()

  const showProving = React.useCallback(
    (proving: Partial<ResourceOfficeBookingListItem>) => {
      router.navigate('provings.details', { id: proving.proving })
    },
    [router]
  )

  const { loading, data, error, refetch } =
    useReadProvingsForResourceBookingOverviewQuery()

  const resourceBookings =
    data && data.provings ? getResourceBookings(data.provings) : []

  useShowCommand(
    { name: 'proving', count: selectedItems.length },
    {
      priority: 10,
      onClick: () => showProving(selectedItems[0]),
    },
    [selectedItems]
  )
  useCommand(
    {
      key: 'timespan',
      priority: 20,
      text: t('model.proving.resourceRequirements.timespan'),
      iconProps: { iconName: 'CalendarWeek' },
      disabled: selectedItems.length === 0,
      subMenuProps: {
        items: [
          {
            key: 'setResourceBookingTimespan',
            text: t('field.timespan.set'),
            onClick: () => {
              showTimespanPanel(
                selectedItems.length === 1
                  ? {
                      dateFrom: selectedItems[0].dateFrom,
                      dateTo: selectedItems[0].dateTo,
                    }
                  : {
                      dateFrom: selectedItems[0].provingDateFrom,
                      dateTo: selectedItems[0].provingDateTo,
                    }
              )
            },
          },
          {
            key: 'setResourceBookingDateToProvingDate',
            text: t('field.timespan.reset'),
            onClick: () => {
              setResourceBookingDateToProvingDate()
            },
          },
        ],
      },
    },
    [selectedItems]
  )

  const handleSubmitTimespan = React.useCallback(
    async (
      values: ResourceBookingUpdateInput,
      actions: FormikActions<ResourceBookingUpdateInput>
    ) => {
      actions.setSubmitting(true)
      const batches: ResourceBookingBatchInput[] = []
      selectedItems.forEach(async (rr) => {
        batches.push({ _id: rr._id, update: values })
      })
      const { errors } = await updateResourceBooking({
        variables: { provingId: selectedItems[0].provingId, batch: batches },
      })
      if (errors) {
        throw errors
      }
      selection.setAllSelected(false)
      refetch()
      actions.setSubmitting(false)
      hideTimespanPanel()
    },
    [
      updateResourceBooking,
      selectedItems,
      refetch,
      selection,
      hideTimespanPanel,
    ]
  )

  const setResourceBookingDateToProvingDate = React.useCallback(async () => {
    const batch: ResourceBookingBatchInput[] = selectedItems.map((sel) => ({
      _id: sel._id,
      update: {
        dateFrom: selectedItems[0].provingDateFrom,
        dateTo: selectedItems[0].provingDateTo,
      },
    }))

    await updateResourceBooking({
      variables: {
        provingId: selectedItems[0].provingId,
        batch,
      },
    })

    refetch()
    hideTimespanPanel()
  }, [selectedItems, hideTimespanPanel, updateResourceBooking, refetch])

  return (
    <FlexSpinner loading={loading} error={error}>
      <ResourceOfficeBookingsViewList
        groupType={GroupType.OfficeLocation}
        items={resourceBookings}
        selection={selection}
      />
      <FormikPanel {...timespanPanelState} onSubmit={handleSubmitTimespan}>
        <ProvingDetailsTimespanForm proving={{}} resource={selectedItems[0]} />
      </FormikPanel>
    </FlexSpinner>
  )
}

export const ResourceOfficeLocationBookingsView: React.FC = () => (
  <>
    <ViewAuthorizer
      roles={[
        UserRole.SystemAdministrator,
        UserRole.ResourceManager,
        UserRole.Reader,
      ]}
    >
      <StickyPane>
        <AppCommandBar />
        <ResourceOfficeLocationBookingsListView />
      </StickyPane>
    </ViewAuthorizer>
  </>
)

const ResourceOfficeDateBookingsListView: React.FunctionComponent = () => {
  const router = useRouter()

  const showProving = React.useCallback(
    (proving: Partial<ResourceOfficeBookingListItem>) => {
      router.navigate('provings.details', { id: proving.proving })
    },
    [router]
  )

  const { loading, data, error, refetch } =
    useReadProvingsForResourceBookingOverviewQuery()

  const { t } = useTranslation()
  const [updateResourceBooking] = useUpdateResourceBookingMutation()

  const resourceBookings =
    data && data.provings ? getResourceBookings(data.provings) : []

  const [timespanPanelState, showTimespanPanel, hideTimespanPanel] =
    useFormikPanelState()

  const [selection, selectedItems] =
    useSelection<ResourceOfficeBookingListItem>({
      getKey: (item) => item._id,
    })

  useShowCommand(
    { name: 'proving', count: selectedItems.length },
    {
      priority: 10,
      onClick: () => showProving(selectedItems[0]),
    },
    [selectedItems]
  )

  useCommand(
    {
      key: 'timespan',
      priority: 20,
      text: t('model.proving.resourceRequirements.timespan'),
      iconProps: { iconName: 'CalendarWeek' },
      disabled: selectedItems.length === 0,
      subMenuProps: {
        items: [
          {
            key: 'setResourceBookingTimespan',
            text: t('field.timespan.set'),
            onClick: () => {
              showTimespanPanel(
                selectedItems.length === 1
                  ? {
                      dateFrom: selectedItems[0].dateFrom,
                      dateTo: selectedItems[0].dateTo,
                    }
                  : {
                      dateFrom: selectedItems[0].provingDateFrom,
                      dateTo: selectedItems[0].provingDateTo,
                    }
              )
            },
          },
          {
            key: 'setResourceBookingDateToProvingDate',
            text: t('field.timespan.reset'),
            onClick: () => {
              setResourceBookingDateToProvingDate()
            },
          },
        ],
      },
    },
    [selectedItems]
  )

  const handleSubmitTimespan = React.useCallback(
    async (
      values: ResourceBookingUpdateInput,
      actions: FormikActions<ResourceBookingUpdateInput>
    ) => {
      actions.setSubmitting(true)
      const batches: ResourceBookingBatchInput[] = []
      selectedItems.forEach(async (rr) => {
        batches.push({ _id: rr._id, update: values })
      })
      const { errors } = await updateResourceBooking({
        variables: { provingId: selectedItems[0].provingId, batch: batches },
      })
      if (errors) {
        throw errors
      }
      selection.setAllSelected(false)
      refetch()
      actions.setSubmitting(false)
      hideTimespanPanel()
    },
    [
      updateResourceBooking,
      selectedItems,
      refetch,
      selection,
      hideTimespanPanel,
    ]
  )

  const setResourceBookingDateToProvingDate = React.useCallback(async () => {
    const batch: ResourceBookingBatchInput[] = selectedItems.map((sel) => ({
      _id: sel._id,
      update: {
        dateFrom: selectedItems[0].provingDateFrom,
        dateTo: selectedItems[0].provingDateTo,
      },
    }))

    await updateResourceBooking({
      variables: {
        provingId: selectedItems[0].provingId,
        batch,
      },
    })

    refetch()
    hideTimespanPanel()
  }, [selectedItems, hideTimespanPanel, updateResourceBooking, refetch])

  return (
    <FlexSpinner loading={loading} error={error}>
      <ResourceOfficeBookingsViewList
        groupType={GroupType.OfficeDate}
        items={resourceBookings}
      />
      <FormikPanel {...timespanPanelState} onSubmit={handleSubmitTimespan}>
        <ProvingDetailsTimespanForm proving={{}} resource={selectedItems[0]} />
      </FormikPanel>
    </FlexSpinner>
  )
}

export const ResourceOfficeDateBookingsView: React.FC = () => (
  <>
    <ViewAuthorizer
      roles={[
        UserRole.SystemAdministrator,
        UserRole.ResourceManager,
        UserRole.Reader,
      ]}
    >
      <StickyPane>
        <AppCommandBar />
        <ResourceOfficeDateBookingsListView />
      </StickyPane>
    </ViewAuthorizer>
  </>
)

const getVehicles = (provings: any): ResourceVehicleBookingListItem[] => {
  const vehicles: ResourceVehicleBookingListItem[] = []
  provings.forEach((proving: any) => {
    const currentvehicles = proving ? proving.vehicles : []
    currentvehicles.forEach((vehicle: Vehicle) => {
      const item: ResourceVehicleBookingListItem = vehicle
      item.provingTitel = proving.title
      item.proving = proving.id
      item.responsible = proving.responsible
        ? proving.responsible[0]
        : undefined
      item.provingDateFrom = proving.dateFrom
      item.provingDateTo = proving.dateTo
      item.provingId = proving._id
      vehicles.push(item)
    })
  })

  return vehicles
}

const ResourceVehicleModellBookingsListView: React.FunctionComponent = () => {
  const { t } = useTranslation()
  const router = useRouter()
  const [updateVehicles] = useUpdateVehiclesMutation()
  const [timespanPanelState, showTimespanPanel, hideTimespanPanel] =
    useFormikPanelState()

  const showProving = React.useCallback(
    (proving: Partial<ResourceVehicleBookingListItem>) => {
      router.navigate('provings.details', { id: proving.proving })
    },
    [router]
  )

  const { loading, data, error, refetch } =
    useReadProvingsForResourceBookingOverviewQuery()

  const [selection, selectedItems] =
    useSelection<ResourceVehicleBookingListItem>({
      getKey: (item) => item._id,
    })

  useShowCommand(
    { name: 'proving', count: selectedItems.length },
    {
      priority: 10,
      onClick: () => showProving(selectedItems[0]),
    },
    [selectedItems]
  )

  const vehicles = data && data.provings ? getVehicles(data.provings) : []

  const handleSubmitUpdate = React.useCallback(
    async (values: VehicleUpdateInput, actions: FormikActions<any>) => {
      actions.setSubmitting(true)
      const batch: VehicleBatchInput[] = selectedItems.map((sel) => ({
        _id: sel._id,
        update: values,
      }))

      await updateVehicles({
        variables: { batch, provingId: selectedItems[0].proving as any },
      })

      actions.setSubmitting(false)
      selection.setAllSelected(false)
      refetch()
      hideTimespanPanel()
    },
    [selection, selectedItems, refetch, updateVehicles, hideTimespanPanel]
  )

  const setTimespanDateToProvingDate = React.useCallback(async () => {
    const batch: VehicleBatchInput[] = selectedItems.map((sel) => ({
      _id: sel._id,
      update: {
        dateFrom: selectedItems[0].provingDateFrom,
        dateTo: selectedItems[0].provingDateTo,
      },
    }))

    await updateVehicles({
      variables: {
        provingId: selectedItems[0].proving as any,
        batch,
      },
    })

    refetch()
  }, [selectedItems, updateVehicles, refetch])

  useCommand(
    {
      key: 'timespan',
      disabled: selectedItems.length === 0,
      text: t('model.proving.vehicle.timespan'),
      iconProps: { iconName: 'CalendarWeek' },
      subMenuProps: {
        items: [
          {
            key: 'setTimespan',
            text: t('field.timespan.set'),
            onClick: () => {
              showTimespanPanel(
                selectedItems.length === 1
                  ? {
                      dateFrom: selectedItems[0].dateFrom,
                      dateTo: selectedItems[0].dateTo,
                    }
                  : {
                      dateFrom: selectedItems[0].provingDateFrom,
                      dateTo: selectedItems[0].provingDateTo,
                    }
              )
            },
          },
          {
            key: 'setDateToProvingDate',
            text: t('field.timespan.reset'),
            onClick: () => {
              setTimespanDateToProvingDate()
            },
          },
        ],
      },
    },
    [selectedItems]
  )

  return (
    <FlexSpinner loading={loading} error={error}>
      <ResourceVehicleBookingsViewList
        groupType={GroupType.VehicleModell}
        items={vehicles}
        selection={selection}
      />
      <FormikPanel {...timespanPanelState} onSubmit={handleSubmitUpdate}>
        <ProvingDetailsTimespanForm proving={{}} resource={selectedItems[0]} />
      </FormikPanel>
    </FlexSpinner>
  )
}

export const ResourceVehicleModellBookingsView: React.FC = () => (
  <>
    <ViewAuthorizer
      roles={[
        UserRole.SystemAdministrator,
        UserRole.ResourceManager,
        UserRole.Reader,
      ]}
    >
      <StickyPane>
        <AppCommandBar />
        <ResourceVehicleModellBookingsListView />
      </StickyPane>
    </ViewAuthorizer>
  </>
)

const ResourceVehicleNumberBookingsListView: React.FunctionComponent = () => {
  const router = useRouter()
  const [updateVehicles] = useUpdateVehiclesMutation()
  const { t } = useTranslation()
  const showProving = React.useCallback(
    (proving: Partial<ResourceVehicleBookingListItem>) => {
      router.navigate('provings.details', { id: proving.proving })
    },
    [router]
  )

  const { loading, data, error, refetch } =
    useReadProvingsForResourceBookingOverviewQuery()

  const [selection, selectedItems] =
    useSelection<ResourceVehicleBookingListItem>({
      getKey: (item) => item._id,
    })

  const [timespanPanelState, showTimespanPanel, hideTimespanPanel] =
    useFormikPanelState()

  const handleSubmitUpdate = React.useCallback(
    async (values: VehicleUpdateInput, actions: FormikActions<any>) => {
      actions.setSubmitting(true)
      const batch: VehicleBatchInput[] = selectedItems.map((sel) => ({
        _id: sel._id,
        update: values,
      }))

      await updateVehicles({
        variables: { batch, provingId: selectedItems[0].proving as any },
      })

      actions.setSubmitting(false)
      selection.setAllSelected(false)
      refetch()
      hideTimespanPanel()
    },
    [selection, selectedItems, refetch, updateVehicles, hideTimespanPanel]
  )

  const setTimespanDateToProvingDate = React.useCallback(async () => {
    const batch: VehicleBatchInput[] = selectedItems.map((sel) => ({
      _id: sel._id,
      update: {
        dateFrom: selectedItems[0].provingDateFrom,
        dateTo: selectedItems[0].provingDateTo,
      },
    }))

    await updateVehicles({
      variables: {
        provingId: selectedItems[0].proving as any,
        batch,
      },
    })

    refetch()
  }, [selectedItems, updateVehicles, refetch])

  useCommand(
    {
      key: 'timespan',
      disabled: selectedItems.length === 0,
      text: t('model.proving.vehicle.timespan'),
      iconProps: { iconName: 'CalendarWeek' },
      subMenuProps: {
        items: [
          {
            key: 'setTimespan',
            text: t('field.timespan.set'),
            onClick: () => {
              showTimespanPanel(
                selectedItems.length === 1
                  ? {
                      dateFrom: selectedItems[0].dateFrom,
                      dateTo: selectedItems[0].dateTo,
                    }
                  : {
                      dateFrom: selectedItems[0].provingDateFrom,
                      dateTo: selectedItems[0].provingDateTo,
                    }
              )
            },
          },
          {
            key: 'setDateToProvingDate',
            text: t('field.timespan.reset'),
            onClick: () => {
              setTimespanDateToProvingDate()
            },
          },
        ],
      },
    },
    [selectedItems]
  )

  useShowCommand(
    { name: 'proving', count: selectedItems.length },
    {
      priority: 10,
      onClick: () => showProving(selectedItems[0]),
    },
    [selectedItems]
  )

  const vehicles = data && data.provings ? getVehicles(data.provings) : []

  return (
    <FlexSpinner loading={loading} error={error}>
      <ResourceVehicleBookingsViewList
        groupType={GroupType.VehicleNumber}
        items={vehicles}
        selection={selection}
      />
      <FormikPanel {...timespanPanelState} onSubmit={handleSubmitUpdate}>
        <ProvingDetailsTimespanForm proving={{}} resource={selectedItems[0]} />
      </FormikPanel>
    </FlexSpinner>
  )
}

export const ResourceVehicleNumberBookingsView: React.FC = () => (
  <>
    <ViewAuthorizer
      roles={[
        UserRole.SystemAdministrator,
        UserRole.ResourceManager,
        UserRole.Reader,
      ]}
    >
      <StickyPane>
        <AppCommandBar />
        <ResourceVehicleNumberBookingsListView />
      </StickyPane>
    </ViewAuthorizer>
  </>
)
